import React, { memo, useState } from 'react';
import { availabilityText } from '@/utils/availability';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Image } from 'expo-image';
import { Ionicons } from '@expo/vector-icons';
import { router, Href } from 'expo-router';
import { Shop } from '@/data/types';
import { colors, radius, shadows, spacing, type, useAppTheme } from '@/theme';
import { RatingStars } from '../ui/RatingStars';
import { PriceTag } from '../ui/PriceTag';
import { Badge } from '../ui/Badge';
import { FitText } from '../ui/FitText';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { toggleFavoriteShop } from '@/store/slices/favoritesSlice';
import { Avatar } from '../ui/Avatar';
import { formatDistance, formatDistanceShort } from '@/utils/geo';
import { ScaledSheet, useCardScale, useRowCardWidth } from '@/theme/responsive';
import { trText } from '@/i18n/plain';
import { useLanguage } from '@/i18n';

interface Props {
  shop: Shop;
  variant?: 'compact' | 'list';
  // Independent stylists are merged into the same search/nearby/popular/recommended results as
  // shops server-side (see service-business's TenantService.searchPublic) but don't have a Tenant
  // to open at /shop/[id] — this redirects the tap to their real profile route instead.
  href?: Href;
  width?: number;
  /** Search results: the service or category that matched the search, shown as "Matches: …". */
  matchedOn?: string | null;
  /** Called when the card is opened, before navigating (used to count taps on search results). */
  onOpen?: () => void;
}

/** Used across Home (nearby/recommended rows), Discover, Search results, Favorites, and
 *  Nearby/Map's bottom sheet — the "compact" variant fits a horizontal scroll row, "list"
 *  is a full-width row for vertical lists. */
// Memoized — these render by the dozen in horizontal/vertical scroll rows across Home, Discover,
// Search, and Favorites, and re-rendered on every parent state change without it (e.g. typing in
// an unrelated search field re-rendering every visible card).
function ShopCardComponent({ shop, variant = 'compact', href, width, matchedOn, onOpen }: Props) {
  useLanguage(); // redraw when the language changes (trText is read at call time)
  const { colors } = useAppTheme();
  const dispatch = useAppDispatch();
  // A little bigger on a tablet, laptop or monitor, more the wider the window is.
  const grow = useCardScale();
  /**
   * Always the shop bucket — including for a solo stylist listed among the shops (kind
   * 'independent_stylist'), who used to be filed under stylistIds from here.
   *
   * A favourite belongs to the surface it was tapped on: hearting a shop listing puts it in
   * Favorites -> Shops, hearting the same stylist's own card (StylistCard, /stylist/[id]) puts it in
   * Favorites -> Stylists. For a solo stylist those are two independent hearts rather than one
   * lighting up the other, which is what made favouriting a "shop" silently populate the Stylists
   * tab. Favorites -> Shops renders these through their profile, since /tenants/:id 404s for them.
   */
  const isFavorite = useAppSelector((state) => state.favorites.shopIds.includes(shop.id));
  const isCompact = variant === 'compact';
  const [page, setPage] = useState(0);

  // Sized so that exactly two cards fit side by side across the screen with margins
  const rowWidth = useRowCardWidth();
  const cardWidth = width ?? rowWidth;
  // Compact cards are sized from the screen, so the photo follows the card's width instead of staying a fixed height
  // (a tall photo on a narrow card looked like a stretched tile).
  // A phone-sized card (two across) gets a wide, shallow photo and a short distance ("1.0 km"), so it is not a tall block.
  const compactCard = isCompact && cardWidth < 190;
  const photoHeight = Math.max(66, Math.min(170, Math.round(cardWidth * (compactCard ? (cardWidth < 170 ? 0.5 : 0.56) : 0.68))));

  // Cover first, then the rest of the gallery. For a solo stylist the gallery is their service
  // photos (see service-business TenantService.toSearchResult), so the cover is usually the first
  // of them — deduped so it isn't shown twice.
  const photos = [...new Set([shop.image, ...shop.gallery].filter(Boolean))];
  // Matches styles.compact width / styles.listImageWrap width below.
  const pageWidth = isCompact ? cardWidth : 110;
  /**
   * Swipeable only on the list variant. Compact cards sit inside a horizontal row (Home's preview,
   * Discover's old carousels), and a horizontal pager nested in a horizontal scroller loses every
   * swipe to the parent on Android. List cards sit in vertical lists, so there is nothing to fight
   * — the gallery works there without any gesture workaround.
   */
  const showGallery = !isCompact && photos.length > 1;

  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => {
        onOpen?.();
        router.push(href ?? { pathname: '/shop/[id]', params: { id: shop.id } });
      }}
      style={[
        styles.card,
        { backgroundColor: colors.surface, borderColor: colors.border },
        isCompact ? [styles.compact, { width: cardWidth }] : styles.list,
      ]}>
      <View
        style={[
          isCompact ? [styles.compactImageWrap, { height: photoHeight }] : styles.listImageWrap,
          { backgroundColor: colors.surfaceMuted },
        ]}>
        {photos.length === 0 ? (
          // No photo uploaded yet (common for a brand-new independent stylist) — an initials avatar
          // reads as "no photo set" instead of looking like a broken/missing image.
          <View style={[StyleSheet.absoluteFill, styles.imageFallback]}>
            <Avatar name={shop.name} size={isCompact ? Math.max(30, Math.round(photoHeight * 0.45)) : 44} />
          </View>
        ) : !showGallery ? (
          <Image source={{ uri: photos[0] }} style={StyleSheet.absoluteFill} contentFit="cover" transition={150} />
        ) : (
          <>
            {/* Paged rather than free-scrolling so a swipe always lands on a whole photo. Page
                width is the card's own fixed width, so no onLayout measurement is needed and
                there's no first-render jump. */}
            <ScrollView
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              onMomentumScrollEnd={(e) =>
                setPage(Math.round(e.nativeEvent.contentOffset.x / pageWidth))
              }
              style={StyleSheet.absoluteFill}>
              {photos.map((uri) => (
                <Image
                  key={uri}
                  source={{ uri }}
                  style={{ width: pageWidth, height: '100%' }}
                  contentFit="cover"
                  transition={150}
                />
              ))}
            </ScrollView>
            {/* pointerEvents none so the dots never swallow a swipe aimed at the photo. */}
            <View style={styles.dotsRow} pointerEvents="none">
              {photos.map((uri, i) => (
                <View key={uri} style={[styles.pageDot, i === page && styles.pageDotActive]} />
              ))}
            </View>
          </>
        )}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          onPress={(e) => {
            e.stopPropagation();
            dispatch(toggleFavoriteShop(shop.id));
          }}
          style={[styles.favBtn, { backgroundColor: colors.surface }]}>
          <Ionicons name={isFavorite ? 'heart' : 'heart-outline'} size={16} color={isFavorite ? colors.error : colors.textPrimary} />
        </Pressable>
        <Badge label={availabilityText(shop.availability)?.badge ?? 'Open'} tone={availabilityText(shop.availability) ? 'warning' : 'success'} style={styles.openBadge} />
        {isCompact && shop.offerCount ? (
          // The offer count rides on the photo, so a shop with offers is the same height as one without.
          <View style={styles.offerChip} pointerEvents="none">
            <Ionicons name="pricetag" size={10} color={colors.white} />
            <Text style={styles.offerChipText} numberOfLines={1}>{shop.offerCount}</Text>
          </View>
        ) : null}
      </View>

      <View style={[styles.body, isCompact && styles.compactBody]}>
        {isCompact ? (
          <FitText width={cardWidth - 20} base={14} min={11} style={[styles.compactTitle, { color: colors.textPrimary }]}>{shop.name}</FitText>
        ) : (
          <Text style={[type.titleLg, { color: colors.textPrimary }]} numberOfLines={1}>{shop.name}</Text>
        )}
        {matchedOn && !isCompact ? (
          <View style={styles.matchRow}>
            <Ionicons name="search-outline" size={12} color={colors.accentDark} />
            <Text style={[type.caption, styles.matchText, { color: colors.accentDark }]} numberOfLines={1}>
              {trText('Matches:', 'মিলেছে:')} {matchedOn}
            </Text>
          </View>
        ) : null}
        {shop.offerCount && !isCompact ? (
          <View style={styles.offerRow}>
            <Ionicons name="pricetag" size={12} color={colors.success} />
            <Text style={[type.caption, styles.offerText, { color: colors.success }]} numberOfLines={1}>
              {shop.offerCount === 1 ? trText('1 offer', '১টি অফার') : trText(`${shop.offerCount} offers`, `${shop.offerCount}টি অফার`)}
            </Text>
          </View>
        ) : null}
        <View style={[styles.metaRow, isCompact && styles.metaRowFixed]}>
          <RatingStars rating={shop.rating} reviewCount={shop.reviewCount} shortCount={compactCard} />
          {/* `> 0` hid the nearest results: anything closer than the backend's rounding step came
              through as 0 and was treated as "no distance at all". Only a null means unknown —
              a genuine 0 metres is a real, and very good, answer. */}
          {shop.distanceKm != null && (
            <>
              <View style={[styles.dot, { backgroundColor: colors.textTertiary }]} />
              <Ionicons name="location-outline" size={12} color={colors.textTertiary} />
              <Text style={[type.caption, styles.metaText, { color: colors.textTertiary }]} numberOfLines={1}>{compactCard ? formatDistanceShort(shop.distanceKm) : formatDistance(shop.distanceKm)}</Text>
            </>
          )}
        </View>
        {shop.startingPrice > 0 && (
          <View style={styles.bottomRow}>
            {/* Always "onwards", never the distance: the meta row above already shows distance
                next to a location pin, so repeating it here rendered "20 m away" twice on the
                same card. "onwards" is the piece of information the price alone doesn't carry. */}
            <PriceTag price={shop.startingPrice} suffix="onwards" size="sm" />
          </View>
        )}
        {shop.nextAvailable && !isCompact ? (
          <Text style={[type.caption, styles.nextAvailable, { color: colors.info }]} numberOfLines={1}>
            <Ionicons name="time-outline" size={11} color={colors.info} />{' '}
            {isCompact
                ? shop.nextAvailable
                : trText(`Next available: ${shop.nextAvailable}`, `নেক্সট খালি সময়: ${shop.nextAvailable}`)}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
}

export const ShopCard = memo(ShopCardComponent);

const styles = ScaledSheet.create({
  card: { backgroundColor: colors.surface, borderRadius: radius.lg, borderWidth: 1, borderColor: colors.border, overflow: 'hidden', ...shadows.card },
  compact: {},
  list: { width: '100%', flexDirection: 'row' },
  compactImageWrap: { height: 112, backgroundColor: colors.surfaceMuted },
  listImageWrap: { width: 110, backgroundColor: colors.surfaceMuted },
  imageFallback: { alignItems: 'center', justifyContent: 'center' },
  favBtn: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
    ...shadows.card,
  },
  openBadge: { position: 'absolute', bottom: 6, left: 6 },
  dotsRow: { position: 'absolute', bottom: spacing.sm, left: 0, right: 0, flexDirection: 'row', justifyContent: 'center', gap: 4 },
  pageDot: { width: 5, height: 5, borderRadius: 2.5, backgroundColor: 'rgba(255,255,255,0.55)' },
  pageDotActive: { backgroundColor: colors.white, width: 12 },
  body: { padding: 10, gap: 4, flex: 1 },
  compactTitle: { fontSize: 14, fontWeight: '700', lineHeight: 18, color: colors.textPrimary },
  offerRow: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  offerText: { flexShrink: 1 },
  matchRow: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  matchText: { flexShrink: 1 },
  metaRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', columnGap: 4, rowGap: 2 },
  dot: { width: 3, height: 3, borderRadius: 1.5, backgroundColor: colors.textTertiary },
  // Compact cards are one fixed size: the body never grows with its text.
  compactBody: { flex: 0, height: 76, overflow: 'hidden' },
  metaRowFixed: { flexWrap: 'nowrap', overflow: 'hidden' },
  offerChip: { position: 'absolute', top: 6, left: 6, flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: colors.success, borderRadius: 10, paddingHorizontal: 6, paddingVertical: 2 },
  offerChipText: { color: colors.white, fontSize: 10, fontWeight: '700' },
  metaText: { color: colors.textTertiary, fontSize: 11, flexShrink: 1 },
  bottomRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 1 },
  nextAvailable: { color: colors.info, marginTop: 1, fontSize: 11 },
});
